import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Cpu,
  Brain,
  Wand2,
  Tag,
  FileText,
  Image,
  CheckCircle,
  AlertCircle,
  Clock,
  Zap,
  Eye,
  Lightbulb,
  TrendingUp,
  Download,
  ChevronDown,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { useAIMetadata, useAICapabilities, AIMetadataExtractionOptions } from '../../hooks/useAIMetadata';

interface AIMetadataExtractionProps {
  mediaId: string;
  className?: string;
  onMetadataExtracted?: (metadata: any) => void;
}

interface ExtractionOptionsProps {
  options: AIMetadataExtractionOptions;
  onChange: (options: AIMetadataExtractionOptions) => void;
  capabilities: any;
}

const ExtractionOptions: React.FC<ExtractionOptionsProps> = ({ options, onChange, capabilities }) => {
  const handleOptionChange = (key: keyof AIMetadataExtractionOptions, value: boolean) => {
    onChange({ ...options, [key]: value });
  };

  const optionConfig = [
    {
      key: 'useAI' as const,
      label: 'AI-Enhanced Analysis',
      description: 'Use AI to generate smart titles, descriptions, and keywords',
      icon: Brain,
      color: 'text-purple-400',
      bgColor: 'bg-purple-400/10',
      available: capabilities?.aiServices?.openai?.enabled || capabilities?.aiServices?.google?.enabled
    },
    {
      key: 'extractThumbnail' as const,
      label: 'Generate Thumbnails',
      description: 'Create multiple thumbnail sizes for video and image files',
      icon: Image,
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
      available: true
    },
    {
      key: 'analyzeContent' as const,
      label: 'Content Analysis',
      description: 'Detect objects, text, faces, and analyze audio content',
      icon: Eye,
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
      available: capabilities?.aiServices?.google?.enabled || capabilities?.aiServices?.aws?.enabled
    },
    {
      key: 'generateKeywords' as const,
      label: 'Smart Keywords',
      description: 'Generate intelligent keywords for better discoverability',
      icon: Tag,
      color: 'text-orange-400',
      bgColor: 'bg-orange-400/10',
      available: capabilities?.aiServices?.openai?.enabled
    },
    {
      key: 'enhanceExisting' as const,
      label: 'Enhance Existing',
      description: 'Add to existing metadata without replacing current data',
      icon: TrendingUp,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-400/10',
      available: true
    }
  ];

  return (
    <div className="space-y-3">
      {optionConfig.map(({ key, label, description, icon: Icon, color, bgColor, available }) => (
        <div
          key={key}
          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
            available 
              ? 'border-white/20 hover:border-white/30 cursor-pointer' 
              : 'border-gray-600 opacity-50 cursor-not-allowed'
          }`}
          onClick={() => available && handleOptionChange(key, !options[key])}
        >
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-white">{label}</h4>
              {!available && (
                <span className="px-2 py-1 bg-gray-600 text-gray-300 text-xs rounded-full">
                  Unavailable
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-1">{description}</p>
          </div>
          {available && (
            <div className="flex-shrink-0">
              <input
                type="checkbox"
                checked={options[key] || false}
                onChange={(e) => handleOptionChange(key, e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

interface ExtractionHistoryProps {
  mediaId: string;
  history: any[];
  onViewDetails: (extraction: any) => void;
}

const ExtractionHistory: React.FC<ExtractionHistoryProps> = ({ 
  mediaId, 
  history, 
  onViewDetails 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (history.length === 0) {
    return (
      <div className="text-center py-6 text-gray-400">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No extraction history available</p>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full text-left font-medium text-white mb-3 hover:text-blue-300 transition-colors"
      >
        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        Extraction History ({history.length})
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-2 overflow-hidden"
          >
            {history.slice(0, 5).map((extraction) => (
              <div
                key={extraction.id}
                className="cosmic-glass rounded-lg p-3 hover:bg-white/10 transition-colors cursor-pointer"
                onClick={() => onViewDetails(extraction)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-medium text-white">
                      {extraction.extraction_type}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(extraction.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {extraction.ai_enhanced_metadata?.ai_generated_keywords && (
                      <span className="text-xs px-2 py-1 bg-blue-400/20 text-blue-300 rounded-full">
                        {extraction.ai_enhanced_metadata.ai_generated_keywords.length} keywords
                      </span>
                    )}
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  </div>
                </div>
                {extraction.ai_enhanced_metadata?.ai_generated_title && (
                  <p className="text-xs text-gray-300 mt-1 truncate">
                    Title: {extraction.ai_enhanced_metadata.ai_generated_title}
                  </p>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface QuickActionsProps {
  mediaId: string;
  onAction: (action: string) => void;
  isLoading: boolean;
}

const QuickActions: React.FC<QuickActionsProps> = ({ mediaId, onAction, isLoading }) => {
  const actions = [
    {
      key: 'keywords',
      label: 'Generate Keywords',
      description: 'Create smart keywords from existing metadata',
      icon: Tag,
      color: 'bg-orange-600 hover:bg-orange-700'
    },
    {
      key: 'title',
      label: 'Enhance Title',
      description: 'Generate an AI-improved title',
      icon: Lightbulb,
      color: 'bg-yellow-600 hover:bg-yellow-700'
    },
    {
      key: 'full',
      label: 'Full Extraction',
      description: 'Complete AI metadata analysis',
      icon: Sparkles,
      color: 'bg-purple-600 hover:bg-purple-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {actions.map(({ key, label, description, icon: Icon, color }) => (
        <button
          key={key}
          onClick={() => onAction(key)}
          disabled={isLoading}
          className={`flex items-center gap-3 p-4 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${color}`}
        >
          <Icon className="w-5 h-5" />
          <div className="text-left">
            <div className="font-medium text-sm">{label}</div>
            <div className="text-xs opacity-75">{description}</div>
          </div>
        </button>
      ))}
    </div>
  );
};

export const AIMetadataExtraction: React.FC<AIMetadataExtractionProps> = ({
  mediaId,
  className,
  onMetadataExtracted
}) => {
  const [extractionOptions, setExtractionOptions] = useState<AIMetadataExtractionOptions>({
    useAI: true,
    extractThumbnail: true,
    analyzeContent: true,
    generateKeywords: true,
    enhanceExisting: false
  });

  const [selectedExtraction, setSelectedExtraction] = useState<any>(null);

  const {
    extractionHistory,
    isExtracting,
    isGeneratingKeywords,
    isEnhancingTitle,
    extractionError,
    extractMetadata,
    generateKeywords,
    enhanceTitle,
    lastExtraction,
    lastKeywords,
    lastTitleEnhancement,
    refetchHistory
  } = useAIMetadata(mediaId);

  const { data: capabilities, isLoading: isLoadingCapabilities } = useAICapabilities();

  const handleExtractMetadata = async () => {
    try {
      const result = await extractMetadata(extractionOptions);
      if (onMetadataExtracted) {
        onMetadataExtracted(result.metadata);
      }
      refetchHistory();
    } catch (error) {
      console.error('Extraction failed:', error);
    }
  };

  const handleQuickAction = async (action: string) => {
    try {
      switch (action) {
        case 'keywords':
          await generateKeywords();
          break;
        case 'title':
          await enhanceTitle();
          break;
        case 'full':
          await extractMetadata({
            useAI: true,
            extractThumbnail: true,
            analyzeContent: true,
            generateKeywords: true,
            enhanceExisting: false
          });
          if (onMetadataExtracted && lastExtraction) {
            onMetadataExtracted(lastExtraction.metadata);
          }
          break;
      }
      refetchHistory();
    } catch (error) {
      console.error('Quick action failed:', error);
    }
  };

  const isAnyLoading = isExtracting || isGeneratingKeywords || isEnhancingTitle;

  if (isLoadingCapabilities) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-white/10 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`cosmic-glass rounded-lg overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-400/20 rounded-lg">
              <Sparkles className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">AI Metadata Extraction</h2>
              <p className="text-sm text-gray-400">Enhance your media with AI-powered insights</p>
            </div>
          </div>

          {isAnyLoading && (
            <div className="flex items-center gap-2 text-blue-400">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm">Processing...</span>
            </div>
          )}
        </div>

        {/* AI Service Status */}
        {capabilities && (
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(capabilities.aiServices).map(([service, config]: [string, any]) => (
              <span
                key={service}
                className={`px-2 py-1 text-xs rounded-full ${
                  config.enabled 
                    ? 'bg-green-400/20 text-green-300' 
                    : 'bg-gray-600/20 text-gray-400'
                }`}
              >
                {service.toUpperCase()} {config.enabled ? 'Online' : 'Offline'}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Quick Actions */}
        <div>
          <h3 className="text-lg font-medium text-white mb-3">Quick Actions</h3>
          <QuickActions
            mediaId={mediaId}
            onAction={handleQuickAction}
            isLoading={isAnyLoading}
          />
        </div>

        {/* Extraction Options */}
        <div>
          <h3 className="text-lg font-medium text-white mb-3">Extraction Options</h3>
          <ExtractionOptions
            options={extractionOptions}
            onChange={setExtractionOptions}
            capabilities={capabilities}
          />

          <div className="mt-4">
            <button
              onClick={handleExtractMetadata}
              disabled={isAnyLoading}
              className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Cpu className="w-4 h-4" />
              {isExtracting ? 'Extracting...' : 'Start AI Extraction'}
            </button>
          </div>
        </div>

        {/* Results Display */}
        {(lastExtraction || lastKeywords || lastTitleEnhancement) && (
          <div>
            <h3 className="text-lg font-medium text-white mb-3">Latest Results</h3>
            
            {lastTitleEnhancement && (
              <div className="cosmic-glass rounded-lg p-4 mb-3">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-2">Enhanced Title</h4>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-400">Original:</span>
                        <p className="text-sm text-gray-300">{lastTitleEnhancement.originalTitle}</p>
                      </div>
                      <div>
                        <span className="text-xs text-green-400">Enhanced:</span>
                        <p className="text-sm text-white font-medium">{lastTitleEnhancement.enhancedTitle}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {lastKeywords && (
              <div className="cosmic-glass rounded-lg p-4 mb-3">
                <div className="flex items-start gap-3">
                  <Tag className="w-5 h-5 text-orange-400 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-2">Generated Keywords</h4>
                    <div className="flex flex-wrap gap-1">
                      {lastKeywords.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-600/20 text-blue-300 text-xs rounded-full"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {lastExtraction && (
              <div className="cosmic-glass rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-2">Full Extraction Complete</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      {lastExtraction.metadata.aiEnhanced.ai_generated_keywords && (
                        <div>
                          <div className="text-lg font-bold text-blue-400">
                            {lastExtraction.metadata.aiEnhanced.ai_generated_keywords.length}
                          </div>
                          <div className="text-xs text-gray-400">Keywords</div>
                        </div>
                      )}
                      {lastExtraction.metadata.contentAnalysis.detected_objects && (
                        <div>
                          <div className="text-lg font-bold text-green-400">
                            {lastExtraction.metadata.contentAnalysis.detected_objects.length}
                          </div>
                          <div className="text-xs text-gray-400">Objects</div>
                        </div>
                      )}
                      {lastExtraction.metadata.thumbnails && (
                        <div>
                          <div className="text-lg font-bold text-purple-400">
                            {lastExtraction.metadata.thumbnails.length}
                          </div>
                          <div className="text-xs text-gray-400">Thumbnails</div>
                        </div>
                      )}
                      <div>
                        <div className="text-lg font-bold text-orange-400">
                          {lastExtraction.metadata.aiEnhanced.mood_tags?.length || 0}
                        </div>
                        <div className="text-xs text-gray-400">Mood Tags</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {extractionError && (
          <div className="cosmic-glass rounded-lg p-4 border border-red-400/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-medium text-red-400 mb-1">Extraction Failed</h4>
                <p className="text-sm text-gray-300">{extractionError.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Extraction History */}
        <div>
          <ExtractionHistory
            mediaId={mediaId}
            history={extractionHistory}
            onViewDetails={setSelectedExtraction}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default AIMetadataExtraction;